/**
 * Sidebar for Trade Communications: All Inbox, All Limbo, and per-account folders
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail01, Inbox01, AlertTriangle, ChevronRight, ChevronDown, RefreshCw01, AlertCircle } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useProcessAccountEmails, useProcessAllEmailAccounts } from "@/hooks/api/use-email-accounts";
import { AuthErrorModal } from "@/components/application/email/auth-error-modal";
import { toast } from "sonner";
import type { TradeCommsFolder } from "./types";
import type { EmailAccountResponse } from "@/types/api/email";
import { EmailAccountStatus } from "@/types/api/email";

export interface TradeCommsSidebarProps {
    folder: TradeCommsFolder;
    onFolderChange: (folder: TradeCommsFolder) => void;
    accounts: EmailAccountResponse[];
    limboCountAll: number;
    /** Per-account limbo count (account_id -> count) */
    limboCountByAccount?: Record<string, number>;
    /** Total unread inbox (email activities) */
    inboxUnreadAll?: number;
    /** Per-account unread inbox count (account_id -> count) */
    inboxUnreadByAccount?: Record<string, number>;
    /** Sidebar collapsed (show only toggle strip) */
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
    className?: string;
}

export function TradeCommsSidebar({
    folder,
    onFolderChange,
    accounts,
    limboCountAll,
    limboCountByAccount = {},
    inboxUnreadAll = 0,
    inboxUnreadByAccount = {},
    collapsed = false,
    onCollapsedChange,
    className,
}: TradeCommsSidebarProps) {
    const { t } = useTranslation(["pages:activity"]);
    const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null);
    const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
    const [authErrorAccount, setAuthErrorAccount] = useState<EmailAccountResponse | null>(null);

    const processAccountMutation = useProcessAccountEmails();
    const processAllAccountsMutation = useProcessAllEmailAccounts();

    const isInbox = folder.type === "inbox";
    const isLimbo = folder.type === "limbo";
    const isAccount = folder.type === "account";

    // When folder is an account folder (e.g. from URL on refresh), expand that account so "All account email" / "Review Needed" are visible
    useEffect(() => {
        if (folder.type === "account") {
            setExpandedAccounts((prev) =>
                prev.has(folder.accountId) ? prev : new Set([...prev, folder.accountId])
            );
        }
    }, [folder]);

    const toggleAccount = (accountId: string) => {
        setExpandedAccounts(prev => {
            const next = new Set(prev);
            if (next.has(accountId)) {
                next.delete(accountId);
            } else {
                next.add(accountId);
            }
            return next;
        });
    };

    const handleAuthErrorClick = (account: EmailAccountResponse) => {
        setAuthErrorAccount(account);
    };

    // Handle single account sync
    const handleSyncAccount = async (accountId: string, emailAddress: string) => {
        setSyncingAccountId(accountId);
        try {
            await processAccountMutation.mutateAsync(accountId);
            toast.success(`Sincronizzazione avviata per ${emailAddress}`);
        } catch (error) {
            toast.error("Errore durante la sincronizzazione");
        } finally {
            setSyncingAccountId(null);
        }
    };

    // Handle sync all accounts
    const handleSyncAll = async () => {
        try {
            await processAllAccountsMutation.mutateAsync();
            toast.success("Sincronizzazione avviata per tutti gli account");
        } catch (error) {
            toast.error("Errore durante la sincronizzazione");
        }
    };

    if (collapsed) {
        return (
            <aside
                className={className ?? "w-12 min-w-[48px] flex flex-col border-r border-secondary bg-primary/50 rounded-l-xl overflow-hidden shrink-0"}
                aria-label={t("tradeComms.sidebarTitle", { defaultValue: "Trade Communications" })}
            >
                <Tooltip title={t("tradeComms.expandSidebar", { defaultValue: "Expand sidebar" })} placement="right">
                    <TooltipTrigger
                        className="p-2 border-b border-secondary flex items-center justify-center text-tertiary hover:text-primary hover:bg-secondary/50 transition-colors w-full"
                        onPress={() => onCollapsedChange?.(false)}
                    >
                        <ChevronRight className="h-5 w-5" />
                    </TooltipTrigger>
                </Tooltip>
            </aside>
        );
    }

    return (
        <aside className={className ?? "w-full max-w-[25%] min-w-[200px] flex flex-col border-r border-secondary bg-primary/50 rounded-l-xl overflow-hidden"}>
            <div className="px-2 py-2 border-b border-secondary flex items-center justify-between gap-1">
                <h2 className="text-sm font-semibold text-primary flex items-center gap-2 min-w-0">
                    <Mail01 className="h-4 w-4 text-brand-secondary shrink-0" />
                    <span className="truncate">{t("tradeComms.sidebarTitle", { defaultValue: "Trade Communications" })}</span>
                </h2>
                <div className="flex items-center gap-1 shrink-0">
                    <Tooltip title={t("tradeComms.syncAll", { defaultValue: "Sync All Accounts" })} placement="bottom">
                        <Button
                            size="sm"
                            color="tertiary"
                            onClick={handleSyncAll}
                            isDisabled={processAllAccountsMutation.isPending || accounts.length === 0}
                            className="p-1"
                        >
                            <RefreshCw01 className={`h-3.5 w-3.5 ${processAllAccountsMutation.isPending ? 'animate-spin' : ''}`} />
                        </Button>
                    </Tooltip>
                    {onCollapsedChange && (
                        <Tooltip title={t("tradeComms.collapseSidebar", { defaultValue: "Collapse sidebar" })} placement="bottom">
                            <TooltipTrigger
                                className="p-1 rounded text-tertiary hover:text-primary hover:bg-secondary/50 shrink-0"
                                onPress={() => onCollapsedChange(true)}
                                aria-label={t("tradeComms.collapseSidebar", { defaultValue: "Collapse sidebar" })}
                            >
                                <ChevronRight className="h-4 w-4 rotate-180" />
                            </TooltipTrigger>
                        </Tooltip>
                    )}
                </div>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-1.5 space-y-0.5">
                <button
                    type="button"
                    onClick={() => onFolderChange({ type: "inbox" })}
                    className={`w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${isInbox ? "bg-brand-secondary/20 text-brand-primary font-medium" : "text-secondary hover:bg-secondary/50"
                        }`}
                >
                    <Inbox01 className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{t("tradeComms.allInbox", { defaultValue: "All Inbox Email" })}</span>
                    {inboxUnreadAll > 0 && (
                        <Badge type="pill-color" size="sm" color="brand">
                            {inboxUnreadAll}
                        </Badge>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => onFolderChange({ type: "limbo" })}
                    className={`w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${isLimbo ? "bg-warning-100 dark:bg-warning-950/30 text-warning-700 dark:text-warning-400 font-medium" : "text-secondary hover:bg-secondary/50"
                        }`}
                >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{t("tradeComms.allLimbo", { defaultValue: "All Limbo Email" })}</span>
                    {limboCountAll > 0 && (
                        <Badge type="pill-color" size="sm" color="warning">
                            {limboCountAll}
                        </Badge>
                    )}
                </button>

                {accounts.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-secondary">
                        <p className="px-2 py-1 text-xs font-medium text-tertiary uppercase tracking-wide">
                            {t("tradeComms.accounts", { defaultValue: "Account" })}
                        </p>
                        {accounts.map((account) => {
                            const isThisAccount = isAccount && folder.accountId === account.account_id;
                            const accountAll: TradeCommsFolder = { type: "account", accountId: account.account_id, email: account.email_address, sub: "all" };
                            const accountLimbo: TradeCommsFolder = { type: "account", accountId: account.account_id, email: account.email_address, sub: "limbo" };
                            const accLimboCount = limboCountByAccount[account.account_id] ?? 0;
                            const accInboxUnread = inboxUnreadByAccount[account.account_id] ?? 0;
                            const isExpanded = expandedAccounts.has(account.account_id);
                            const hasAuthError = account.status === EmailAccountStatus.AUTHENTICATION_REQUIRED || account.status === EmailAccountStatus.ERROR;

                            return (
                                <div key={account.account_id} className="space-y-0.5">
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => toggleAccount(account.account_id)}
                                            className={`flex-1 flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${isThisAccount ? "bg-secondary/70 text-primary font-medium" : "text-secondary hover:bg-secondary/50"
                                                }`}
                                        >
                                            {isExpanded ? (
                                                <ChevronDown className="h-4 w-4 shrink-0 text-tertiary" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" />
                                            )}
                                            <span className="truncate flex-1">{account.email_address}</span>
                                        </button>
                                        {hasAuthError ? (
                                            <button
                                                onClick={() => handleAuthErrorClick(account)}
                                                title={account.last_error || t("tradeComms.authError", { defaultValue: "Authentication Error" })}
                                                className="p-1 shrink-0 rounded hover:bg-error-secondary/20 transition-colors"
                                            >
                                                <AlertCircle className="h-4 w-4 text-error-primary" />
                                            </button>
                                        ) : (
                                            <Tooltip title={t("tradeComms.syncAccount", { defaultValue: "Sync Account" })} placement="bottom">
                                                <Button
                                                    size="sm"
                                                    color="tertiary"
                                                    onClick={() => handleSyncAccount(account.account_id, account.email_address)}
                                                    isDisabled={syncingAccountId === account.account_id}
                                                    className="p-1 shrink-0"
                                                >
                                                    <RefreshCw01 className={`h-3 w-3 ${syncingAccountId === account.account_id ? 'animate-spin' : ''}`} />
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div>
                                    {isExpanded && (
                                        <div className="pl-5 space-y-0.5">
                                            <button
                                                type="button"
                                                onClick={() => onFolderChange(accountAll)}
                                                className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${isThisAccount && folder.sub === "all" ? "bg-brand-secondary/20 text-brand-primary font-medium" : "text-tertiary hover:bg-secondary/50"
                                                    }`}
                                            >
                                                <span className="truncate flex-1">{t("tradeComms.accountAll", { defaultValue: "All account email" })}</span>
                                                {accInboxUnread > 0 && (
                                                    <Badge type="pill-color" size="sm" color="brand">
                                                        {accInboxUnread}
                                                    </Badge>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onFolderChange(accountLimbo)}
                                                className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${isThisAccount && folder.sub === "limbo" ? "bg-warning-100 dark:bg-warning-950/30 text-warning-700 dark:text-warning-400 font-medium" : "text-tertiary hover:bg-secondary/50"
                                                    }`}
                                            >
                                                <span className="truncate flex-1">{t("tradeComms.reviewNeeded", { defaultValue: "Review Needed" })}</span>
                                                {accLimboCount > 0 && (
                                                    <Badge type="pill-color" size="sm" color="warning">
                                                        {accLimboCount}
                                                    </Badge>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </nav>
            
            {/* Auth Error Modal */}
            <AuthErrorModal
                isOpen={authErrorAccount !== null}
                onClose={() => setAuthErrorAccount(null)}
                account={authErrorAccount}
            />
        </aside>
    );
}
